import config from '../config';
import { acquireApiToken } from '../auth/msal';

/**
 * API client with authentication support.
 *
 * The access token comes from the SHARED MSAL instance (src/auth/msal.js),
 * so the client always uses the same session as the React tree. A silent
 * token is used when possible; on expiry MSAL performs an interactive
 * redirect to re-authenticate. In local dev (no client id configured),
 * acquireApiToken() returns null and requests go out unauthenticated —
 * the backend bypasses auth via SKIP_AUTH.
 */

/**
 * @deprecated Retained for backward compatibility. The client now imports the
 * shared MSAL instance directly, so wiring one in is no longer required.
 */
export function setMsalInstance() {
  /* no-op */
}

async function getAccessToken() {
  return acquireApiToken();
}

/**
 * Ensure every query-string VALUE is properly percent-encoded.
 *
 * Most call sites interpolate `timeRange` straight into the URL
 * (`?timeRange=${timeRange}`). That's fine for presets like `24h`, but a
 * custom range token looks like
 *   custom:2026-08-01T00:00:00.000Z:2026-08-02T00:00:00.000Z
 * whose colons make the query string ambiguous/malformed, so the backend
 * never sees a valid value and the page returns no data. Rather than touch
 * ~30 call sites, we normalize here: decode-then-encode each value, which is
 * idempotent for values already encoded via encodeURIComponent (api, email,
 * ids…) and correctly encodes raw tokens like the custom range.
 */
function normalizeQuery(path) {
  const qIndex = path.indexOf('?');
  if (qIndex === -1) return path;
  const base = path.slice(0, qIndex);
  const query = path.slice(qIndex + 1);
  if (!query) return path;

  const normalized = query
    .split('&')
    .map((pair) => {
      const eq = pair.indexOf('=');
      if (eq === -1) return pair;
      const key = pair.slice(0, eq);
      const rawVal = pair.slice(eq + 1);
      let decoded;
      try {
        decoded = decodeURIComponent(rawVal);
      } catch {
        // Value had a stray % that isn't a valid escape; encode as-is.
        decoded = rawVal;
      }
      return `${key}=${encodeURIComponent(decoded)}`;
    })
    .join('&');

  return `${base}?${normalized}`;
}

async function apiFetch(path, options = {}) {
  const url = `${config.apiBaseUrl}${normalizeQuery(path)}`;

  const doFetch = async (token) => {
    const headers = { 'Content-Type': 'application/json', ...options.headers };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return fetch(url, { ...options, headers });
  };

  let token = await getAccessToken();
  let response = await doFetch(token);

  // If the server rejects the token (expired/revoked between cache and call),
  // force a fresh token — silently first, then via interactive redirect — and
  // retry exactly once before surfacing the error.
  if ((response.status === 401 || response.status === 403) && token) {
    const freshToken = await acquireApiToken({ forceRefresh: true });
    if (freshToken && freshToken !== token) {
      token = freshToken;
      response = await doFetch(token);
    }
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    const error = new Error(body.error || `API Error ${response.status}`);
    error.status = response.status;
    throw error;
  }

  if (response.status === 204) return null;
  return response.json();
}

/**
 * Build a query string from an object, dropping empty/undefined entries and
 * URL-encoding every value. Hand-rolled `?a=${x}` concatenation is how
 * injection bugs and double-`?` URLs creep in once an endpoint grows a
 * second optional parameter.
 */
function qs(params) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params || {})) {
    if (value !== undefined && value !== null && value !== '') search.set(key, value);
  }
  const str = search.toString();
  return str ? `?${str}` : '';
}

export const api = {
  // Dashboard
  getDashboardSummary: (timeRange) => apiFetch(`/dashboard/summary?timeRange=${timeRange}`),
  getDashboardTimeSeries: (timeRange, granularity) =>
    apiFetch(`/dashboard/timeseries?timeRange=${timeRange}&granularity=${granularity}`),
  getTopProducts: (timeRange, top = 5) => apiFetch(`/dashboard/top-products?timeRange=${timeRange}&top=${top}`),
  getTopUsers: (timeRange, top = 5) => apiFetch(`/dashboard/top-users?timeRange=${timeRange}&top=${top}`),
  getTopModels: (timeRange, top = 5) => apiFetch(`/dashboard/top-models?timeRange=${timeRange}&top=${top}`),
  getTokenTimeSeries: (timeRange, granularity) =>
    apiFetch(`/dashboard/token-timeseries?timeRange=${timeRange}&granularity=${granularity}`),
  getLatencyPercentiles: (timeRange, api) =>
    apiFetch(`/dashboard/latency?timeRange=${timeRange}${api ? `&api=${encodeURIComponent(api)}` : ''}`),
  getLatencyDistribution: (timeRange, bucketSize = 100) =>
    apiFetch(`/dashboard/latency-distribution?timeRange=${timeRange}&bucketSize=${bucketSize}`),
  getGatewayErrors: (timeRange) => apiFetch(`/dashboard/gateway-errors?timeRange=${timeRange}`),
  getCacheAnalysis: (timeRange) => apiFetch(`/dashboard/cache-analysis?timeRange=${timeRange}`),

  // Token efficiency & truncation (prompt:completion ratio, truncation rate).
  getTokenEfficiency: (timeRange, granularity = '1h') =>
    apiFetch(`/dashboard/token-efficiency?timeRange=${timeRange}&granularity=${granularity}`),
  // Responsible AI — content-safety posture (blocked/flagged/jailbreak + coverage).
  getContentSafety: (timeRange, granularity = '1h') =>
    apiFetch(`/dashboard/content-safety?timeRange=${timeRange}&granularity=${granularity}`),
  // The actual calls where a content-safety signal was caught (drill-down).
  // kind ∈ blocked | jailbreak | flagged | all.
  getContentSafetyEvents: (timeRange, kind = 'flagged', limit = 200) =>
    apiFetch(`/dashboard/content-safety/events?timeRange=${timeRange}&kind=${kind}&limit=${limit}`),
  // Full gateway-log detail (request/response bodies) for one caught call,
  // addressed by Correlation ID — opens the actual log from the events modal.
  getContentSafetyCall: (correlationId, timeRange = '30d') =>
    apiFetch(`/dashboard/content-safety/call/${encodeURIComponent(correlationId)}?timeRange=${timeRange}`),
  // The actual calls behind the AI-efficiency KPIs (truncated / prompt-bloat).
  // kind ∈ truncated | bloat | all. Rows carry a Correlation ID → reuse
  // getContentSafetyCall() to open the full gateway log.
  getTokenEfficiencyEvents: (timeRange, kind = 'truncated', limit = 200) =>
    apiFetch(`/dashboard/token-efficiency/events?timeRange=${timeRange}&kind=${kind}&limit=${limit}`),
  // Security posture — auth failures (401/403) + 404-flood hygiene signal.
  getSecurity: (timeRange, granularity = '1h') =>
    apiFetch(`/dashboard/security?timeRange=${timeRange}&granularity=${granularity}`),
  // The actual security events behind the KPIs. kind ∈ auth | flood.
  // Rows carry a Correlation ID → reuse getContentSafetyCall() for the log.
  getSecurityEvents: (timeRange, kind = 'auth', limit = 200) =>
    apiFetch(`/dashboard/security/events?timeRange=${timeRange}&kind=${kind}&limit=${limit}`),
  // Error taxonomy — root-cause classification of genuine failures.
  getErrorTaxonomy: (timeRange, granularity = '1h') =>
    apiFetch(`/dashboard/error-taxonomy?timeRange=${timeRange}&granularity=${granularity}`),
  // The actual failing calls behind the taxonomy KPIs. category ∈ ErrorCategory
  // label | all. Rows carry a Correlation ID → reuse getContentSafetyCall().
  getErrorTaxonomyEvents: (timeRange, category = 'all', limit = 200) =>
    apiFetch(`/dashboard/error-taxonomy/events?timeRange=${timeRange}&category=${encodeURIComponent(category)}&limit=${limit}`),
  // Products
  getProducts: (timeRange = '24h') => apiFetch(`/products?timeRange=${timeRange}`),
  getProduct: (id) => apiFetch(`/products/${encodeURIComponent(id)}`),
  getProductAnalytics: (id, timeRange, granularity) =>
    apiFetch(`/products/${encodeURIComponent(id)}/analytics?timeRange=${timeRange}&granularity=${granularity}`),

  // APIs
  getApis: (timeRange) => apiFetch(`/apis?timeRange=${timeRange}`),
  getApiTimeSeries: (id, timeRange, granularity) =>
    apiFetch(`/apis/${encodeURIComponent(id)}/timeseries?timeRange=${timeRange}&granularity=${granularity}`),
  getApiHeatmap: (id, timeRange) => apiFetch(`/apis/${encodeURIComponent(id)}/heatmap?timeRange=${timeRange}`),
  getApiErrors: (id, timeRange) => apiFetch(`/apis/${encodeURIComponent(id)}/errors?timeRange=${timeRange}`),

  // Errors & usage with source/application-level drill-down
  getErrorsByProduct: (timeRange = '24h') =>
    apiFetch(`/apis/errors/by-product?timeRange=${timeRange}`),
  getErrorsByApp: (timeRange = '24h') =>
    apiFetch(`/apis/errors/by-app?timeRange=${timeRange}`),
  getErrorEvents: (dimension, value, timeRange = '24h', limit = 200) =>
    apiFetch(
      `/apis/errors/events?dimension=${encodeURIComponent(dimension)}&value=${encodeURIComponent(value)}&timeRange=${timeRange}&limit=${limit}`
    ),
  getErrorCall: (correlationId, timeRange = '30d') =>
    apiFetch(`/apis/errors/call/${encodeURIComponent(correlationId)}?timeRange=${timeRange}`),

  // Real-time
  getRealtimeCalls: (limit = 100) => apiFetch(`/realtime/calls?limit=${limit}`),
  getRealtimeStats: () => apiFetch('/realtime/stats'),

  // Conversations
  getConversations: (subscriptionId, timeRange, limit) =>
    apiFetch(`/conversations?subscriptionId=${encodeURIComponent(subscriptionId)}&timeRange=${timeRange}&limit=${limit}`),
  getConversationTotals: (subscriptionId, timeRange) =>
    apiFetch(`/conversations/totals?subscriptionId=${encodeURIComponent(subscriptionId)}&timeRange=${timeRange}`),
  getCallDetail: (operationId) => apiFetch(`/conversations/${encodeURIComponent(operationId)}`),
  getConversationSubscriptions: (timeRange = '7d', limit = 200) =>
    apiFetch(`/conversations/subscriptions?timeRange=${timeRange}&limit=${limit}`),
  getRecentConversations: (timeRange = '24h', limit = 100) =>
    apiFetch(`/conversations/recent?timeRange=${timeRange}&limit=${limit}`),
  getConversationApps: (ids = []) => {
    const list = (Array.isArray(ids) ? ids : [ids])
      .filter(Boolean)
      .map((s) => String(s).trim())
      .filter(Boolean);
    if (list.length === 0) return Promise.resolve([]);
    return apiFetch(`/conversations/apps?ids=${encodeURIComponent(list.join(','))}`);
  },

  // Costs
  getCostBreakdown: (timeRange) => apiFetch(`/costs/breakdown?timeRange=${timeRange}`),
  getCostForecast: (timeRange, forecastDays) =>
    apiFetch(`/costs/forecast?timeRange=${timeRange}&forecastDays=${forecastDays}`),
  getCostBySubscription: (timeRange) => apiFetch(`/costs/by-subscription?timeRange=${timeRange}`),
  // Per-Foundry-resource detail. Each row carries an embedded `byModel`
  // array and a `daily` cost series, so the Cost Center detail panel
  // can render everything from a single response.
  getCostByResource: (timeRange) => apiFetch(`/costs/by-resource?timeRange=${timeRange}`),
  getCostByModel: (timeRange) => apiFetch(`/costs/by-model?timeRange=${timeRange}`),
  // Estimated LLM cost attributed to each APIM Product (tokens × pricing).
  // NOTE: computed, not billed — Cost Management can't split by product.
  getCostByProduct: (timeRange) => apiFetch(`/costs/by-product?timeRange=${timeRange}`),
  // Tier 4 — attribute Foundry $ to the APIM subscription that consumed
  // each call (joins Cost Management $ with KQL token data by model/day).
  // `apimService` (optional) narrows the attribution to a single APIM
  // instance in the resource group. Omitting it rolls up every instance.
  getCostByApimSubscription: (timeRange, apimService) => {
    const params = new URLSearchParams({ timeRange });
    if (apimService) params.set('apimService', apimService);
    return apiFetch(`/costs/by-apim-subscription?${params.toString()}`);
  },
  // Azure Cost Management — actual billing data
  getAzureBilling: (timeRange) => apiFetch(`/costs/azure-billing?timeRange=${timeRange}`),

  // Users
  getUsers: (timeRange) => apiFetch(`/users?timeRange=${timeRange}`),
  getUserActivity: (email, timeRange) =>
    apiFetch(`/users/${encodeURIComponent(email)}/activity?timeRange=${timeRange}`),
  // How the gateway is being accessed (user token / app token / API key).
  getAuthBreakdown: (timeRange) => apiFetch(`/users/auth-breakdown?timeRange=${timeRange}`),
  // Application callers (app-token traffic), keyed on appClientId.
  getAppCallers: (timeRange) => apiFetch(`/users/apps?timeRange=${timeRange}`),
  getAppActivity: (appClientId, timeRange) =>
    apiFetch(`/users/apps/${encodeURIComponent(appClientId)}/activity?timeRange=${timeRange}`),

  // Settings — Pricing
  getAllPricing: () => apiFetch('/settings/pricing'),
  getModelPricing: (modelId) => apiFetch(`/settings/pricing/${encodeURIComponent(modelId)}`),
  createPricing: (data) => apiFetch('/settings/pricing', { method: 'POST', body: JSON.stringify(data) }),
  deletePricing: (id, modelId) =>
    apiFetch(`/settings/pricing/${encodeURIComponent(id)}?modelId=${encodeURIComponent(modelId)}`, { method: 'DELETE' }),
  seedPricing: () => apiFetch('/settings/pricing/seed', { method: 'POST' }),

  // Settings — Cache
  clearCache: () => apiFetch('/settings/cache/clear', { method: 'POST' }),

  // Model Test Portal (admin only)
  getModelTestApis: (apimService) =>
    apiFetch(`/model-test/apis${apimService ? `?apimService=${encodeURIComponent(apimService)}` : ''}`),
  runModelTest: (data) => apiFetch('/model-test/test', { method: 'POST', body: JSON.stringify(data) }),
  runModelTestSuite: (data) =>
    apiFetch('/model-test/test/suite', { method: 'POST', body: JSON.stringify(data) }),

  // APIM Reports
  getReportsByApi: (timeRange, top = 10) => apiFetch(`/reports/by-api?timeRange=${timeRange}&top=${top}`),
  getReportsByProduct: (timeRange, top = 10) => apiFetch(`/reports/by-product?timeRange=${timeRange}&top=${top}`),
  getReportsBySubscription: (timeRange, top = 20) => apiFetch(`/reports/by-subscription?timeRange=${timeRange}&top=${top}`),
  getReportsByOperation: (timeRange, top = 20) => apiFetch(`/reports/by-operation?timeRange=${timeRange}&top=${top}`),
  getReportsByGeo: (timeRange) => apiFetch(`/reports/by-geo?timeRange=${timeRange}`),
  getReportsByUser: (timeRange, top = 20) => apiFetch(`/reports/by-user?timeRange=${timeRange}&top=${top}`),
  getReportsByTime: (timeRange, interval = 'PT1H') => apiFetch(`/reports/by-time?timeRange=${timeRange}&interval=${interval}`),
  getMetrics: (timeRange, granularity) => apiFetch(`/reports/metrics?timeRange=${timeRange}&granularity=${granularity}`),

  // APIM instances — populates the per-instance selector on the Cost
  // Center page. Lists every APIM service in the configured resource
  // group; `default` is the env-configured APIM_SERVICE_NAME so the UI
  // can preselect it.
  getApimInstances: () => apiFetch('/apim/instances'),

  // ── Traffic Controls / kill switch (admin only) ─────────────────────
  // Every route below is gated server-side by the admin app role and is
  // audited to Application Insights. `apimService` optionally targets a
  // non-default APIM instance in the resource group.
  //
  // Two independent TIERS are exposed, matching the two tabs in the UI:
  //   • `products` — disables every API and subscription under a product.
  //   • `apis`     — disables a single API, leaving the rest of its
  //                  product serving traffic.
  // They write to SEPARATE named-value namespaces server-side
  // (`disable-*` vs `disable-api-*`), so a product and an API that
  // happen to share a display name can never collide.
  getKillSwitches: (apimService) =>
    apiFetch(`/killswitch/products${qs({ apimService })}`),
  getKillSwitch: (productId, apimService) =>
    apiFetch(`/killswitch/products/${encodeURIComponent(productId)}${qs({ apimService })}`),
  // `reason` is MANDATORY — the backend rejects the write without it.
  // `expectedCurrent` enables optimistic concurrency: pass the state the
  // operator saw so a concurrent change by another admin returns 409
  // instead of silently clobbering their action.
  setKillSwitch: ({ productId, enabled, reason, apimService, expectedCurrent }) =>
    apiFetch(`/killswitch/products/${encodeURIComponent(productId)}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled, reason, apimService, expectedCurrent }),
    }),

  getApiKillSwitches: (apimService) => apiFetch(`/killswitch/apis${qs({ apimService })}`),
  getApiKillSwitch: (apiId, apimService) =>
    apiFetch(`/killswitch/apis/${encodeURIComponent(apiId)}${qs({ apimService })}`),
  setApiKillSwitch: ({ apiId, enabled, reason, apimService, expectedCurrent }) =>
    apiFetch(`/killswitch/apis/${encodeURIComponent(apiId)}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled, reason, apimService, expectedCurrent }),
    }),

  // MCP-server tier — APIM APIs classified as MCP (native type==='mcp' or the
  // /mcp/ naming convention). Separate `disable-mcp-<id>` named-value space.
  getMcpKillSwitches: (apimService) => apiFetch(`/killswitch/mcp${qs({ apimService })}`),
  getMcpKillSwitch: (apiId, apimService) =>
    apiFetch(`/killswitch/mcp/${encodeURIComponent(apiId)}${qs({ apimService })}`),
  setMcpKillSwitch: ({ apiId, enabled, reason, apimService, expectedCurrent }) =>
    apiFetch(`/killswitch/mcp/${encodeURIComponent(apiId)}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled, reason, apimService, expectedCurrent }),
    }),

  // Agent tier — APIM APIs matching the /agent/ naming convention.
  // `disable-agent-<id>` named-value space.
  getAgentKillSwitches: (apimService) => apiFetch(`/killswitch/agents${qs({ apimService })}`),
  getAgentKillSwitch: (apiId, apimService) =>
    apiFetch(`/killswitch/agents/${encodeURIComponent(apiId)}${qs({ apimService })}`),
  setAgentKillSwitch: ({ apiId, enabled, reason, apimService, expectedCurrent }) =>
    apiFetch(`/killswitch/agents/${encodeURIComponent(apiId)}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled, reason, apimService, expectedCurrent }),
    }),

  // Durable kill-switch audit history (Cosmos-backed; outlives App Insights'
  // 90-day retention). `getKillSwitchAudit` → recent across all entities;
  // `getKillSwitchAuditFor` → full history for one entity (tier + id).
  getKillSwitchAudit: (limit = 200) =>
    apiFetch(`/killswitch/audit${qs({ limit })}`),
  getKillSwitchAuditFor: (tier, entityId, limit = 100) =>
    apiFetch(`/killswitch/audit/${encodeURIComponent(tier)}/${encodeURIComponent(entityId)}${qs({ limit })}`),

  // Annotations
  getAnnotations: (start, end) => apiFetch(`/annotations?start=${start || ''}&end=${end || ''}`),
  createAnnotation: (data) => apiFetch('/annotations', { method: 'POST', body: JSON.stringify(data) }),
  deleteAnnotation: (id, type) => apiFetch(`/annotations/${encodeURIComponent(id)}?type=${encodeURIComponent(type)}`, { method: 'DELETE' }),

  // APIM Consumer Onboarding
  getOnboardingEnums: () => apiFetch('/onboarding/enums'),
  getOnboardingPrefill: () => apiFetch('/onboarding/prefill'),
  recommendOnboardingAuth: (payload) =>
    apiFetch('/onboarding/recommend-auth', { method: 'POST', body: JSON.stringify(payload) }),
  lookupOnboardingUser: (email) =>
    apiFetch(`/onboarding/user-lookup${qs({ email })}`),
  getOnboardingByCmdb: (cmdbId) =>
    apiFetch(`/onboarding/cmdb/${encodeURIComponent(cmdbId)}/requests`),
  getApimProductsByCmdb: (cmdbId) =>
    apiFetch(`/onboarding/cmdb/${encodeURIComponent(cmdbId)}/apim-products`),
  submitOnboarding: (payload, { draft = false } = {}) =>
    apiFetch(`/onboarding${draft ? '?draft=true' : ''}`, { method: 'POST', body: JSON.stringify(payload) }),
  // Edit an existing Draft in place (owner only).
  updateOnboarding: (id, payload) =>
    apiFetch(`/onboarding/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(payload) }),
  // Promote an existing Draft to Submitted (applies final edits + validates).
  submitOnboardingDraft: (id, payload) =>
    apiFetch(`/onboarding/${encodeURIComponent(id)}/submit`, { method: 'POST', body: JSON.stringify(payload) }),
  getMyOnboarding: () => apiFetch('/onboarding/mine'),
  getOnboardingQueue: (status) => apiFetch(`/onboarding/queue${qs({ status })}`),
  getAllOnboarding: (status) => apiFetch(`/onboarding/all${qs({ status })}`),
  getOnboardingById: (id, cmdbId) => apiFetch(`/onboarding/${encodeURIComponent(id)}${qs({ cmdbId })}`),
  decideOnboarding: (id, { decision, note, cmdbId }) =>
    apiFetch(`/onboarding/${encodeURIComponent(id)}/decision`, {
      method: 'POST',
      body: JSON.stringify({ decision, note, cmdbId }),
    }),

  // Health
  health: () => apiFetch('/health'),
};

export default api;
